import { Inject, Injectable } from '@angular/core';
import { State, Action, StateContext, Selector, createSelector } from '@ngxs/store';
import {LenderUser, RepOnlineStatus} from 'app/models/dto/lender-user.model';
import { PusherService } from 'app/modules/shared/services/pusher.service';
import { RollbarService } from 'app/rollbar';
import { LenderUserService } from 'app/services/lender-user/lender-user.service';
import { environment } from 'environments/environment';
import Rollbar from 'rollbar';
import { tap } from 'rxjs/operators';
import { catchError, of } from 'rxjs';
import {
  UpdateLenderUser,
  UpdatePausedWorkflow,
  GoOnline,
  GoOffline,
  GetLenderUser,
  GoOfflineSubscription, GoOnlineSubscription
} from './lender-user.actions';
import {patch} from '@ngxs/store/operators';

declare const FS;
declare const pendo;

export class LenderUserStateModel {
  lenderUser: LenderUser | null;
}

@Injectable()
@State<LenderUserStateModel>({
  name: 'lenderUser',
  defaults: {
    lenderUser: null,
  }
})

export class LenderUserState {

  @Selector()
  static lenderUser(state: LenderUserStateModel): LenderUser | null {
    return state.lenderUser;
  }

  @Selector()
  static hasPermissions(permissionNames: string[]) {
    return createSelector([LenderUserState], ({lenderUser}) => {
      const permissions = lenderUser?.lenderUser?.permissions;
      return permissionNames.reduce(( res, cur ) => {
        res[cur] = permissions?.includes(cur);
        return res;
      }, {});
    });
  }

  @Selector()
  static hasPermission(permissionName: string) {
    return createSelector([LenderUserState], ({lenderUser}) => {
      const permissions = lenderUser?.lenderUser?.permissions;
      return permissions.includes(permissionName);
    });
  }

  @Selector()
  static teamMembers(state: LenderUserStateModel) {
    return state.lenderUser?.team.lender_users;
  }

  @Selector()
  static online(state: LenderUserStateModel) {
    const onlineStatus = state.lenderUser?.online_status;
    const onlineExpires = onlineStatus?.expires || null;
    const mountainTimeString = new Date().toLocaleString('en-US', { timeZone: 'America/Denver' });
    return onlineExpires ?
      new Date(onlineExpires) > new Date(mountainTimeString) :
      false;
  }

  private identified = false;

  constructor(
    private lenderUserService: LenderUserService,
    private pusherService: PusherService,
    @Inject(RollbarService) private rollbar: Rollbar
  ) { }

  @Action(GetLenderUser)
  getLenderUser({ patchState }: StateContext<LenderUserStateModel>, {}: GetLenderUser) {
    return this.lenderUserService.get().pipe(
      catchError(() => {
        return of();
      }),
      tap(lenderUser => {
        if (!lenderUser) {
          return;
        }
        patchState({ lenderUser });
        setTimeout(() => {
          this.identify(lenderUser);
        }, 1000);
      })
    );
  }

  @Action(UpdateLenderUser)
  updateLenderUser(
    { patchState, getState }: StateContext<LenderUserStateModel>,
    { userId, settings }: UpdateLenderUser
  ) {
    return this.lenderUserService.update(userId, settings).pipe(
      tap(lenderUser => {
        patchState({ lenderUser: {
          ...getState().lenderUser, ...lenderUser
        } });
      }));
  }

  @Action(UpdatePausedWorkflow)
  updatePausedWorkflow(
    { patchState, getState }: StateContext<LenderUserStateModel>,
    { userId, meta }: UpdatePausedWorkflow
  ) {
    return this.lenderUserService.updatePausedWorkflow(userId, meta).pipe(
      tap(lenderUser => {
        patchState({ lenderUser: {
          ...getState().lenderUser, ...lenderUser
        } });
      }));
  }

  @Action(GoOnline)
  GoOnline(
    { getState, patchState }: StateContext<LenderUserStateModel>,
    { userId }: GoOnline
  ) {
    this.lenderUserService.changeOnlineStatus(userId, 'online')
      .pipe(catchError( error => {
        console.error(error);
        return of(error);
      }))
      .subscribe((online_status: RepOnlineStatus) => {
        const updatedLenderUser = getState().lenderUser;
        if (updatedLenderUser) {
          updatedLenderUser.online_status = online_status;
          patchState({lenderUser: updatedLenderUser});
        }
      });
  }

  @Action(GoOffline)
  GoOffline(
    { getState, patchState }: StateContext<LenderUserStateModel>,
    { userId }: GoOffline
  ) {
    this.lenderUserService.changeOnlineStatus(userId, 'offline')
      .pipe(catchError( error => {
        console.error(error);
        return of(error);
      }))
      .subscribe(() => {
        const updatedLenderUser = getState().lenderUser;
        if (updatedLenderUser) {
          updatedLenderUser.online_status = undefined;
          patchState({lenderUser: updatedLenderUser});
        }
      });
  }

  @Action(GoOnlineSubscription)
  goOnlineSubscription(
    { }: StateContext<LenderUserStateModel>,
    { userId }: GoOnlineSubscription
  ) {
    this.pusherService.subscribeRaw(`presence-lendio-${environment.environment}-notification-channel`);
    this.pusherService.subscribeRaw(`presence-lender-user-${userId}-${environment.environment}-notification-channel`);
  }

  @Action(GoOfflineSubscription)
  goOfflineSubscription(
    { }: StateContext<LenderUserStateModel>,
    { userId }: GoOfflineSubscription
  ) {
    this.pusherService.unsubscribeRaw(`presence-lendio-${environment.environment}-notification-channel`);
    this.pusherService.unsubscribeRaw(`presence-lender-user-${userId}-${environment.environment}-notification-channel`);
  }

  private identify(lenderUser: LenderUser): void {
    if (this.identified) {
      return;
    }
    this.identified = true;

    try {
      this.rollbarIdentify(lenderUser);
      this.fullstoryIdentify(lenderUser);
      this.pendoIdentify(lenderUser);
    } catch (err) {
      this.rollbar.error(err);
    }
  }

  private rollbarIdentify(lenderUser: LenderUser): void {
    this.rollbar.configure({
      payload: {
        person: {
          id: lenderUser.id,
          username: lenderUser.fullName,
          email: lenderUser.user.email
        }
      }
    });
  }

  private fullstoryIdentify(lenderUser: LenderUser): void {
    if (typeof FS === 'undefined') {
      return;
    }

    FS.identify(lenderUser.id, {
      displayName: lenderUser.fullName,
      email: lenderUser.user.email
    });
  }

  private pendoIdentify(lenderUser: LenderUser): void {
    if (
      !environment.production
      || typeof pendo === 'undefined'
    ) {
      return;
    }

    pendo.initialize({
      visitor: {
        id: lenderUser.id,
        email: lenderUser.user.email,
        full_name: lenderUser.fullName
      },
      account: {
        id: lenderUser.team.id,
        name: lenderUser.team.name
      }
    });
  }
}
